import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter, useSearch } from '@tanstack/react-router';
import { queryKeys, queryUtils } from '@vizit/api';
import { AssetTableFilterOptions } from '@vizit/assets';
import { useAuth } from '@vizit/auth';
import { ScoreLevel } from '@vizit/organisms';
import { ProductTableFilterOptions } from '@vizit/products';
import * as React from 'react';
import { LibraryPage, LibraryTabOptions, PagesProvider } from '../../../pages';
import {
  beforeLoadAuthenticatedValidation,
  getLibraryBreadcrumb,
  navigateWithState,
} from '../../../router/route-utils';
import { NavbarPageTemplate } from '../../../templates';

interface LibrarySearch {
  view: LibraryTabOptions;
  // Product table filters
  [ProductTableFilterOptions.ListingScoreLevel]?: ScoreLevel;
  [ProductTableFilterOptions.Brand]?: string;
  [ProductTableFilterOptions.ProductCategory]?: string;
  [ProductTableFilterOptions.ProductCategoryName]?: string;
  [ProductTableFilterOptions.Search]?: string;
  [ProductTableFilterOptions.Retailer]?: string;

  // Asset table filters
  [AssetTableFilterOptions.ImageType]?: string;
  [AssetTableFilterOptions.ProductCategory]?: string;
  [AssetTableFilterOptions.ProductCategoryName]?: string;
}

export const Route = createFileRoute('/_authenticated/library/')({
  staticData: {
    pageName: 'Library',
  },
  component: RouteComponent,
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  validateSearch: (search: Record<string, unknown>): LibrarySearch => {
    const validValues = Object.values(LibraryTabOptions);

    const view =
      typeof search.view === 'string' && validValues.includes(search.view as LibraryTabOptions)
        ? (search.view as LibraryTabOptions)
        : LibraryTabOptions.Products;

    // Products tab supports these filters
    if (view === LibraryTabOptions.Products) {
      return {
        view,
        [ProductTableFilterOptions.ListingScoreLevel]:
          search.score && typeof search.score === 'string' && search.score !== ScoreLevel.All
            ? (search.score as ScoreLevel)
            : undefined,
        [ProductTableFilterOptions.Brand]: search.brand ? (search.brand as string) : undefined,
        [ProductTableFilterOptions.ProductCategory]: search.categoryId ? (search.categoryId as string) : undefined,
        [ProductTableFilterOptions.ProductCategoryName]: search.categoryName
          ? (search.categoryName as string)
          : undefined,
        [ProductTableFilterOptions.Search]: search.searchTerm ? (search.searchTerm as string) : undefined,
        [ProductTableFilterOptions.Retailer]: search.retailer ? (search.retailer as string) : undefined,
      };
    }

    // Assets tab supports these filters
    if (view === LibraryTabOptions.Assets) {
      return {
        view,
        [AssetTableFilterOptions.ImageType]: search.imageType ? (search.imageType as string) : undefined,
        [AssetTableFilterOptions.ProductCategory]: search.assetCategoryId
          ? (search.assetCategoryId as string)
          : undefined,
        [AssetTableFilterOptions.ProductCategoryName]: search.assetCategoryName
          ? (search.assetCategoryName as string)
          : undefined,
      };
    }

    return { view };
  },
});

function RouteComponent() {
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const queryClient = useQueryClient();
  const { defaultPortfolioId, organizationId } = useAuth();

  const handleOnTabChange = React.useCallback(
    async (key: LibraryTabOptions) => {
      await router.navigate({
        to: Route.fullPath,
        search: (prev: Record<string, unknown>) => {
          const {
            view,
            score,
            brand,
            categoryId,
            categoryName,
            searchTerm,
            searchBy,
            imageType,
            assetCategoryId,
            assetCategoryName,
            ...rest
          } = prev;

          if (key === LibraryTabOptions.Products) {
            return {
              ...rest,
              view: key,
              score: score ? (score as ScoreLevel) : undefined,
              brand: brand ? (brand as string) : undefined,
              categoryId: categoryId ? (categoryId as string) : undefined,
              categoryName: categoryName ? (categoryName as string) : undefined,
              searchTerm: searchTerm ? (searchTerm as string) : undefined,
              searchBy: searchBy ? (searchBy as string) : undefined,
            };
          }

          if (key === LibraryTabOptions.Assets) {
            return {
              ...rest,
              view: key,
              assetCategoryId: assetCategoryId ? (assetCategoryId as string) : undefined,
              assetCategoryName: assetCategoryName ? (assetCategoryName as string) : undefined,
              imageType: imageType ? (imageType as string) : undefined,
            };
          }

          return {
            ...rest,
            view: key,
          };
        },
      });
    },
    [router],
  );

  const handleOnSuccessfulUpload = (id: string) => {
    navigateWithState(router, {
      to: '/library/assets/$id',
      params: { id },
      from: { trail: [getLibraryBreadcrumb('assets')] },
      search: (prev: Record<string, unknown>) => ({ ...prev, view: 'assets' }),
    });

    queryClient.invalidateQueries({
      queryKey: queryKeys.images.infinite({
        portfolio_id: defaultPortfolioId ?? '',
        limit: 12,
        sort_by: 'created_at',
        sort_direction: 'desc',
        parent_id: undefined,
        source: undefined,
        exclude_source: ['generated'],
      }).queryKey,
    });
  };

  const handleOnSuccessfulImport = (id: string) => {
    navigateWithState(router, {
      to: '/library/products/$id',
      params: { id },
      from: { trail: [getLibraryBreadcrumb('products')] },
      search: (prev: Record<string, unknown>) => ({ ...prev, view: 'products' }),
    });

    queryUtils.invalidateAllProducts(queryClient);
  };

  const handleOnProductTableFilterChange = (filter: ProductTableFilterOptions, value?: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => ({ ...prev, [filter]: value }),
    });
  };

  const handleOnAssetTableFilterChange = (filter: AssetTableFilterOptions, value: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => ({ ...prev, [filter]: value }),
    });
  };

  return (
    <PagesProvider
      template={NavbarPageTemplate}
      templateProps={{ currentRoute: '/library', contentClassName: 'mx-0 max-w-none overflow-hidden' }}
    >
      <LibraryPage
        className="pt-7 max-sm:pt-8"
        selectedTab={search.view}
        productTableFilters={{
          [ProductTableFilterOptions.ListingScoreLevel]: search[ProductTableFilterOptions.ListingScoreLevel],
          [ProductTableFilterOptions.Brand]: search[ProductTableFilterOptions.Brand],
          [ProductTableFilterOptions.ProductCategory]: search[ProductTableFilterOptions.ProductCategory],
          [ProductTableFilterOptions.ProductCategoryName]: search[ProductTableFilterOptions.ProductCategoryName],
          [ProductTableFilterOptions.Search]: search[ProductTableFilterOptions.Search],
          [ProductTableFilterOptions.Retailer]: search[ProductTableFilterOptions.Retailer],
        }}
        assetTableFilters={{
          [AssetTableFilterOptions.ImageType]: search[AssetTableFilterOptions.ImageType],
          [AssetTableFilterOptions.ProductCategory]: search[AssetTableFilterOptions.ProductCategory],
          [AssetTableFilterOptions.ProductCategoryName]: search[AssetTableFilterOptions.ProductCategoryName],
        }}
        portfolioId={defaultPortfolioId}
        organizationId={organizationId || undefined}
        onTabChange={handleOnTabChange}
        onSuccessfulUpload={handleOnSuccessfulUpload}
        onSuccessfulImport={handleOnSuccessfulImport}
        onProductTableFilterChange={handleOnProductTableFilterChange}
        onAssetTableFilterChange={handleOnAssetTableFilterChange}
      />
    </PagesProvider>
  );
}
